/* Look Inside reader — Feb-2026 (indie-conv batch2 pt3).
 *
 * Replaces the old collapse-drawer view. Splits the book's sample_text
 * into 3 chapter-sized chunks (best-effort), renders them one page at
 * a time in a clean reading canvas with:
 *   - Font picker (serif novel / sans / dyslexic)
 *   - Dark-mode toggle
 *   - Per-chapter scroll progress bar + Chapter N/3 pill
 *   - Prev / Next chapter controls, sticky Buy CTA
 *   - Abandonment telemetry — beacon-POSTs to
 *     /api/bookstore/lookinside/events on open / chapter_reached /
 *     scroll_depth (throttled) / exit, so admins can see who abandoned
 *     where.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ShoppingBag, ChevronLeft, ChevronRight, Sun, Moon, Type, BookOpen } from 'lucide-react';
import { bookstoreApi } from '@/lib/bookstoreApi';

// Splits text into up to 3 chapter-sized chunks. We prefer to cut on
// paragraph boundaries so a chapter never ends mid-sentence.
function chunkIntoChapters(text, target = 3) {
  const raw = (text || '').trim();
  if (!raw) return [];
  const paragraphs = raw.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length <= 1) {
    // Fallback: naive char split when the sample is one big blob.
    const size = Math.max(600, Math.ceil(raw.length / target));
    const parts = [];
    for (let i = 0; i < raw.length && parts.length < target; i += size) {
      parts.push(raw.slice(i, i + size));
    }
    return parts.length ? parts : [raw];
  }
  const targetLen = Math.ceil(raw.length / target);
  const chapters = [];
  let buf = '';
  for (const p of paragraphs) {
    if (buf && buf.length + p.length > targetLen && chapters.length < target - 1) {
      chapters.push(buf.trim());
      buf = p;
    } else {
      buf = buf ? `${buf}\n\n${p}` : p;
    }
  }
  if (buf) chapters.push(buf.trim());
  return chapters.slice(0, target);
}

const FONTS = [
  { id: 'serif',    label: 'Serif',    family: '"Cormorant Garamond", Georgia, "Times New Roman", serif' },
  { id: 'sans',     label: 'Sans',     family: 'Inter, system-ui, sans-serif' },
  { id: 'reader',   label: 'Readable', family: '"OpenDyslexic", "Atkinson Hyperlegible", "Verdana", sans-serif' },
];

export default function LookInsideReader({ bookId, text, priceLabel, onBuy, buying, onClose }) {
  const chapters = useMemo(() => chunkIntoChapters(text, 3), [text]);
  const total = chapters.length;
  const [idx, setIdx] = useState(0);
  const [fontId, setFontId] = useState(() => {
    try { return localStorage.getItem('bs-lookinside-font') || 'serif'; }
    catch { return 'serif'; }
  });
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem('bs-lookinside-dark') === '1'; }
    catch { return false; }
  });
  const [progress, setProgress] = useState(0);   // 0..1 within current chapter
  const [deepest, setDeepest] = useState(0);

  const bodyRef = useRef(null);
  const sessionRef = useRef(`lir_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`);
  const openedAtRef = useRef(Date.now());
  const lastScrollBeaconRef = useRef(0);

  // Persist font + dark toggles
  useEffect(() => { try { localStorage.setItem('bs-lookinside-font', fontId); } catch {} }, [fontId]);
  useEffect(() => { try { localStorage.setItem('bs-lookinside-dark', dark ? '1' : '0'); } catch {} }, [dark]);

  // Fire OPEN + EXIT lifecycle beacons.
  useEffect(() => {
    bookstoreApi.trackStorefrontEvent({ event_type: 'sample_intent', book_id: bookId });
    bookstoreApi.lookInsideEvent({
      book_id: bookId, event: 'open', chapter_index: 0,
      session_id: sessionRef.current,
    });
    const onUnload = () => {
      bookstoreApi.lookInsideEvent({
        book_id: bookId, event: 'exit',
        chapter_index: idxRef.current,
        scroll_depth: progressRef.current,
        time_spent_ms: Date.now() - openedAtRef.current,
        reached_end: idxRef.current >= total - 1 && progressRef.current > 0.9,
        session_id: sessionRef.current,
      });
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      onUnload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, total]);

  // Keep latest idx / progress in refs so the unload handler can read them.
  const idxRef = useRef(0);
  const progressRef = useRef(0);
  useEffect(() => { idxRef.current = idx; }, [idx]);
  useEffect(() => { progressRef.current = progress; }, [progress]);

  // Track deepest chapter and beacon chapter_reached transitions.
  useEffect(() => {
    if (idx > deepest) setDeepest(idx);
    bookstoreApi.lookInsideEvent({
      book_id: bookId, event: 'chapter_reached',
      chapter_index: idx, session_id: sessionRef.current,
    });
    // Reset scroll each chapter change so progress reflects the new chapter.
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Scroll → progress + throttled beacons.
  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const p = max > 0 ? Math.min(1, el.scrollTop / max) : 1;
    setProgress(p);
    const now = Date.now();
    if (p >= 0.95 && now - lastScrollBeaconRef.current > 3000) {
      lastScrollBeaconRef.current = now;
      bookstoreApi.lookInsideEvent({
        book_id: bookId, event: 'scroll',
        chapter_index: idx, scroll_depth: p,
        session_id: sessionRef.current,
      });
    }
  };

  if (!total) return null;
  const chapter = chapters[idx] || '';
  const font = FONTS.find((f) => f.id === fontId) || FONTS[0];
  const theme = dark
    ? { bg: '#1a1414', ink: '#f4ede4', dim: '#a89f92', border: '#3a2e2e' }
    : { bg: '#faf5eb', ink: '#241a15', dim: '#67564b', border: '#e3d5c1' };

  return (
    <div
      className="mt-4 overflow-hidden rounded-md border transition-colors"
      style={{ borderColor: theme.border, backgroundColor: theme.bg, color: theme.ink }}
      data-testid={`book-look-inside-${bookId}`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2"
           style={{ borderColor: theme.border, backgroundColor: dark ? '#221818' : '#f3e9d6' }}>
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em]"
             style={{ color: theme.dim }}>
          <BookOpen className="h-3 w-3" /> Look inside
          <span
            data-testid={`book-look-inside-chapter-pill-${bookId}`}
            className="ml-1 rounded-full px-2 py-[1px] text-[10px] font-semibold"
            style={{ backgroundColor: dark ? '#3a2828' : '#e3cf9d', color: theme.ink }}
          >
            Chapter {idx + 1} / {total}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Font picker */}
          <div className="mr-2 hidden items-center gap-1 md:flex" role="group" aria-label="Font">
            <Type className="h-3 w-3" style={{ color: theme.dim }} />
            {FONTS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFontId(f.id)}
                data-testid={`book-look-inside-font-${f.id}-${bookId}`}
                aria-pressed={fontId === f.id}
                className="rounded-sm px-1.5 py-[2px] text-[10.5px] font-medium transition"
                style={{
                  backgroundColor: fontId === f.id ? (dark ? '#4d3232' : '#c9a04a') : 'transparent',
                  color: fontId === f.id ? '#fff' : theme.dim,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          {/* Dark toggle */}
          <button
            type="button"
            onClick={() => setDark((v) => !v)}
            data-testid={`book-look-inside-dark-${bookId}`}
            aria-pressed={dark}
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="rounded-sm p-1 transition"
            style={{ color: theme.dim }}
          >
            {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              data-testid={`book-look-inside-close-${bookId}`}
              className="ml-1 rounded-sm px-1.5 py-[2px] text-[10.5px]"
              style={{ color: theme.dim }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full" style={{ backgroundColor: dark ? '#2a1e1e' : '#e8dcc5' }}
           data-testid={`book-look-inside-progress-${bookId}`}>
        <div className="h-full transition-[width] duration-150"
             style={{
               width: `${Math.max(4, Math.round(((idx + progress) / total) * 100))}%`,
               backgroundColor: '#c9a04a',
             }} />
      </div>

      {/* Reading canvas */}
      <div
        ref={bodyRef}
        onScroll={onScroll}
        className="relative max-h-[62vh] overflow-y-auto px-6 py-8 md:px-10"
        style={{ fontFamily: font.family, lineHeight: 1.65, fontSize: '15px' }}
        data-testid={`book-look-inside-body-${bookId}`}
      >
        <p style={{ color: theme.dim, letterSpacing: '0.18em', textTransform: 'uppercase', fontSize: '10px', marginBottom: '1rem' }}>
          Chapter {idx + 1}
        </p>
        <div style={{ whiteSpace: 'pre-line', color: theme.ink }}>{chapter}</div>
        {idx === total - 1 && (
          <div
            data-testid={`book-look-inside-end-${bookId}`}
            className="mt-8 rounded-md border p-4 text-center"
            style={{
              borderColor: theme.border,
              color: theme.dim,
              backgroundColor: dark ? '#221818' : '#f3e9d6',
            }}
          >
            <div className="text-[11px] uppercase tracking-[0.18em]">End of preview</div>
            <div className="mt-1 text-[13.5px]" style={{ color: theme.ink }}>
              Enjoying it? Keep reading with the full book.
            </div>
          </div>
        )}
      </div>

      {/* Footer: chapter nav + buy CTA */}
      <div className="flex items-center justify-between gap-2 border-t px-3 py-2"
           style={{ borderColor: theme.border, backgroundColor: dark ? '#221818' : '#f3e9d6' }}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            data-testid={`book-look-inside-prev-${bookId}`}
            className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-medium disabled:opacity-40"
            style={{ color: theme.ink }}
          >
            <ChevronLeft className="h-3 w-3" /> Prev
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
            disabled={idx === total - 1}
            data-testid={`book-look-inside-next-${bookId}`}
            className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-medium disabled:opacity-40"
            style={{ color: theme.ink }}
          >
            Next <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={onBuy}
          disabled={buying}
          data-testid={`book-look-inside-buy-${bookId}`}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11.5px] font-semibold shadow transition hover:brightness-110 disabled:opacity-60"
          style={{ backgroundColor: '#7a1a24', color: '#fff' }}
        >
          <ShoppingBag className="h-3 w-3" />
          {buying ? 'Starting…' : `Buy for ${priceLabel}`}
        </button>
      </div>
    </div>
  );
}
